import { types, Instance } from 'mobx-state-tree';

/**
 * AppPreferencesStore - Persists UI preferences to localStorage
 * 
 * This store handles user interface preferences that should persist across sessions:
 * - Main sidebar collapse/expand state
 * - Other UI preferences as needed
 * 
 * Data is automatically saved to and loaded from localStorage.
 */
export const AppPreferencesStore = types
  .model('AppPreferencesStore', {
    // Main sidebar collapse state
    isSidebarCollapsed: types.optional(types.boolean, false),
    // Chat sidebar collapse state
    isChatSidebarCollapsed: types.optional(types.boolean, false),
    // Mindmap sidebar collapse state
    isMindmapSidebarCollapsed: types.optional(types.boolean, false),
    // Selected mindmap ID (persists across sessions)
    selectedMindmapId: types.maybeNull(types.string),
    // Selected chat conversation ID (persists across sessions)
    selectedChatId: types.maybeNull(types.string),
    // Mindmap layout customization settings
    mindmapCompactness: types.optional(types.number, 1.0), // 0.5 to 2.0, default 1.0
    mindmapNodeSize: types.optional(types.enumeration(['small', 'medium', 'large']), 'medium'),
    mindmapNodeSpacing: types.optional(types.number, 400), // 320 to 1000, default 400
  })
  .actions((self) => {
    const STORAGE_KEY = 'app-preferences';

    return {
      /**
       * Load preferences from localStorage
       */
      loadFromStorage() {
        try {
          const stored = localStorage.getItem(STORAGE_KEY);
          if (stored) {
            const data = JSON.parse(stored);
            if (typeof data.isSidebarCollapsed === 'boolean') {
              self.isSidebarCollapsed = data.isSidebarCollapsed;
            }
            if (typeof data.isChatSidebarCollapsed === 'boolean') {
              self.isChatSidebarCollapsed = data.isChatSidebarCollapsed;
            }
            if (typeof data.isMindmapSidebarCollapsed === 'boolean') {
              self.isMindmapSidebarCollapsed = data.isMindmapSidebarCollapsed;
            }
            if (data.selectedMindmapId) {
              self.selectedMindmapId = data.selectedMindmapId;
            }
            if (data.selectedChatId) {
              self.selectedChatId = data.selectedChatId;
            }
            // Load mindmap layout settings
            if (typeof data.mindmapCompactness === 'number') {
              self.mindmapCompactness = data.mindmapCompactness;
            }
            if (data.mindmapNodeSize === 'small' || data.mindmapNodeSize === 'medium' || data.mindmapNodeSize === 'large') {
              self.mindmapNodeSize = data.mindmapNodeSize;
            }
            if (typeof data.mindmapNodeSpacing === 'number') {
              self.mindmapNodeSpacing = data.mindmapNodeSpacing;
            }
            console.log('[AppPreferences] Loaded from localStorage:', data);
          }
        } catch (error) {
          console.error('[AppPreferences] Failed to load from localStorage:', error);
        }
      },

      /**
       * Save preferences to localStorage
       */
      saveToStorage() {
        try {
          const data = {
            isSidebarCollapsed: self.isSidebarCollapsed,
            isChatSidebarCollapsed: self.isChatSidebarCollapsed,
            isMindmapSidebarCollapsed: self.isMindmapSidebarCollapsed,
            selectedMindmapId: self.selectedMindmapId,
            selectedChatId: self.selectedChatId,
            mindmapCompactness: self.mindmapCompactness,
            mindmapNodeSize: self.mindmapNodeSize,
            mindmapNodeSpacing: self.mindmapNodeSpacing,
          };
          localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
          console.log('[AppPreferences] Saved to localStorage:', data);
        } catch (error) {
          console.error('[AppPreferences] Failed to save to localStorage:', error);
        }
      },

      /**
       * Set sidebar collapsed state and persist
       */
      setSidebarCollapsed(collapsed: boolean) {
        self.isSidebarCollapsed = collapsed;
        this.saveToStorage();
      },

      /**
       * Toggle sidebar collapsed state and persist
       */
      toggleSidebarCollapsed() {
        self.isSidebarCollapsed = !self.isSidebarCollapsed;
        this.saveToStorage();
      },

      /**
       * Set chat sidebar collapsed state and persist
       */
      setChatSidebarCollapsed(collapsed: boolean) {
        self.isChatSidebarCollapsed = collapsed;
        this.saveToStorage();
      },

      /**
       * Toggle chat sidebar collapsed state and persist
       */
      toggleChatSidebarCollapsed() {
        self.isChatSidebarCollapsed = !self.isChatSidebarCollapsed;
        this.saveToStorage();
      },

      /**
       * Set mindmap sidebar collapsed state and persist
       */
      setMindmapSidebarCollapsed(collapsed: boolean) {
        self.isMindmapSidebarCollapsed = collapsed;
        this.saveToStorage();
      },

      /**
       * Toggle mindmap sidebar collapsed state and persist
       */
      toggleMindmapSidebarCollapsed() {
        self.isMindmapSidebarCollapsed = !self.isMindmapSidebarCollapsed;
        this.saveToStorage();
      },

      /**
       * Set selected mindmap ID and persist
       */
      setSelectedMindmapId(mindmapId: string | null) {
        self.selectedMindmapId = mindmapId;
        this.saveToStorage();
        console.log('[AppPreferences] Selected mindmap:', mindmapId);
      },

      /**
       * Set selected chat ID and persist
       */
      setSelectedChatId(chatId: string | null) {
        self.selectedChatId = chatId;
        this.saveToStorage();
        console.log('[AppPreferences] Selected chat:', chatId);
      },

      /**
       * Set mindmap compactness and persist
       */
      setMindmapCompactness(compactness: number) {
        // Clamp to valid range
        self.mindmapCompactness = Math.max(0.5, Math.min(2.0, compactness));
        this.saveToStorage();
        console.log('[AppPreferences] Mindmap compactness:', self.mindmapCompactness);
      },

      /**
       * Set mindmap node size and persist
       */
      setMindmapNodeSize(size: 'small' | 'medium' | 'large') {
        self.mindmapNodeSize = size;
        this.saveToStorage();
        console.log('[AppPreferences] Mindmap node size:', size);
      },

      /**
       * Set mindmap node spacing and persist
       */
      setMindmapNodeSpacing(spacing: number) {
        // Clamp to valid range
        self.mindmapNodeSpacing = Math.max(320, Math.min(1000, spacing));
        this.saveToStorage();
        console.log('[AppPreferences] Mindmap node spacing:', self.mindmapNodeSpacing);
      },
    };
  });

export interface IAppPreferencesStore extends Instance<typeof AppPreferencesStore> {}

